import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, ChevronRight } from "lucide-react";

const GOLD = "#C9A84C";

const ARTICLES = {
  "wat-is-vasten": {
    tag: "Voeding",
    tagColor: "text-orange-400 bg-orange-500/10 border-orange-500/25",
    title: "Wat is vasten — en werkt het echt?",
    subtitle: "Intermittent fasting uitgelegd: de wetenschap, de protocollen en wat jij er in de praktijk mee kunt.",
    readTime: "6 min",
    date: "April 2025",
    sections: [
      {
        heading: "Wat is vasten?",
        body: `Vasten betekent simpelweg: een bepaalde periode bewust niets eten. Dat klinkt radicaal, maar je doet het eigenlijk al elke nacht — de tijd tussen je laatste maaltijd en je ontbijt is technisch gezien al een vastenperiode. Intermittent fasting (IF) bouwt hierop voort door die vensters bewust te verlengen.

Het gaat niet om wát je eet, maar wanneer. IF is daarmee geen dieet in de traditionele zin, maar een eetpatroon.`,
      },
      {
        heading: "De meest gebruikte protocollen",
        body: `**16:8** — 16 uur vasten, 8 uur eten. Het meest populaire protocol. Je slaat het ontbijt over en eet bijvoorbeeld van 12:00 tot 20:00. Ideaal voor beginners.

**18:6** — Iets strikter: 18 uur vasten, 6 uur eetvenster. Populair bij mensen die al gewend zijn aan 16:8 en meer willen bereiken.

**OMAD (One Meal A Day)** — Eén maaltijd per dag. Extreem en niet voor iedereen geschikt, maar sommige mensen zweren erbij. Vraagt veel discipline en goede voedingskennis om tekorten te vermijden.

**5:2** — Vijf dagen normaal eten, twee dagen zeer caloriearm (~500 kcal). Geschikt voor mensen die niet elke dag willen plannen.`,
      },
      {
        heading: "Wat doet vasten met je lichaam?",
        body: `Na 8–12 uur vasten zijn je glycogeenvoorraden (opgeslagen koolhydraten) uitgeput. Je lichaam schakelt dan over op vetverbranding als primaire energiebron — dit heet ketose. Hierdoor:

• Daalt je insulinespiegel sterk, wat vetverbranding bevordert
• Stijgt de aanmaak van groeihormoon (HGH), wat spierbehoud ondersteunt
• Start het proces van autofagie: het lichaam ruimt beschadigde cellen op
• Verbetert de insulinegevoeligheid op de lange termijn

Veel mensen rapporteren ook een scherper gevoel van concentratie tijdens het vasten, doordat de hersenen efficiënt ketonen gebruiken als brandstof.`,
      },
      {
        heading: "Voordelen van intermittent fasting",
        body: `**Gewichtsverlies en vetverlies** — Het eenvoudigste voordeel: minder eetmomenten = minder kansen om te veel te eten. Gecombineerd met de hormonale effecten maakt dit vasten een effectief hulpmiddel bij caloriebeperking.

**Spierbehoud** — In tegenstelling tot wat veel mensen denken, breekt vasten geen spieren af bij gezonde mensen die voldoende eiwitten eten. Het groeihormoon dat vrijkomt tijdens het vasten beschermt juist de spiermassa.

**Mentale helderheid** — Veel mensen ervaren een "flow-staat" tijdens langere vastenperiodes. Dit wordt verklaard door de stabiele bloedsuikerspiegel en de aanmaak van ketonen.

**Vereenvoudiging van je dag** — Minder maaltijden = minder plannen, bereiden en afwassen. Veel mensen merken dat vasten hun relatie met eten normaliseert.`,
      },
      {
        heading: "Voor wie is vasten NIET geschikt?",
        body: `Vasten is niet voor iedereen een goed idee. Wees voorzichtig of overleg met een arts als je:

• Diabetes type 1 of 2 hebt (insuline- en medicatieaanpassing vereist)
• Zwanger bent of borstvoeding geeft
• Een (voorgeschiedenis van) eetstoornis hebt
• Jonger dan 18 jaar bent
• Bepaalde medicijnen gebruikt die op een volle maag moeten worden ingenomen

Bij twijfel: bespreek het altijd met je huisarts of diëtist.`,
      },
      {
        heading: "Praktische tips om te beginnen",
        body: `1. **Begin met 12:12** — Eet 12 uur, vast 12 uur. Dit is de meest toegankelijke instap.
2. **Verschuif je ontbijt geleidelijk** — Schuif je eerste maaltijd elke week een uur op tot je bij 16:8 zit.
3. **Drink genoeg** — Water, zwarte koffie en ongezoete thee zijn toegestaan tijdens het vasten en helpen de honger te onderdrukken.
4. **Eet voldoende eiwitten** — Minimaal 1,6–2 gram per kilo lichaamsgewicht, verdeeld over je eetvenster.
5. **Niet fanatiek beginnen** — Zeker niet direct OMAD als je nog nooit hebt gevast. Bouw rustig op.`,
      },
      {
        heading: "Conclusie",
        body: `Vasten is geen magisch dieet, maar een krachtig hulpmiddel als het bij jou past. Het helpt bij gewichtsbeheersing, verbetert je insulinegevoeligheid en kan je energieniveau stabiliseren — mits je het slim aanpakt.

Het allerbelangrijkste: de beste eetaanpak is er één die jij volhoudt. Als vasten niet bij jouw leefstijl past, zijn er genoeg andere effectieve strategieën. Maar als het past? Geef het dan vier weken de kans om te wennen.`,
      },
    ],
  },

  "hoe-vaak-trainen": {
    tag: "Training",
    tagColor: "text-blue-400 bg-blue-500/10 border-blue-500/25",
    title: "Hoe vaak moet je trainen — en waarom?",
    subtitle: "De optimale trainingsfrequentie voor spiergroei, kracht en vetverlies — onderbouwd met wetenschap en praktijk.",
    readTime: "7 min",
    date: "April 2025",
    sections: [
      {
        heading: "Het meest gestelde vraag in de sportschool",
        body: `"Hoe vaak moet ik trainen?" — je hoort het constant. En het eerlijke antwoord is: dat hangt af van je niveau, doel, herstelcapaciteit en leefstijl. Maar er zijn wel degelijk wetenschappelijk onderbouwde richtlijnen die voor de meeste mensen goed werken.`,
      },
      {
        heading: "Wat de wetenschap zegt over frequentie",
        body: `Onderzoek toont consistent aan dat een spiergroep minimaal 2 keer per week getraind moet worden voor optimale hypertrofie (spiergroei). Eén keer per week is suboptimaal; meer dan 3–4 keer per week voor dezelfde spiergroep heeft zelden meerwaarde — tenzij je een elite-atleet bent.

De totale wekelijkse trainingsvolume (aantal sets per spiergroep) is minstens zo belangrijk als de frequentie. 10–20 werksets per spiergroep per week is voor de meeste mensen het effectieve bereik.`,
      },
      {
        heading: "Beginner: 2–3 keer per week",
        body: `Als je minder dan een jaar traint, is 2–3 keer per week full body trainen de meest effectieve aanpak. Waarom?

• Je spieren, pezen en gewrichten zijn nog aan het adapteren aan de belasting
• Je neurologische systeem leert de bewegingen nog aan — hier zit veel van de vroege krachtswinst
• Je herstelt sneller dan gevorderde sporters, waardoor je vaker kunt trainen
• Full body sessies geven je spiergroepen de 2–3 keer per week stimulus die ze nodig hebben

Een typisch beginnerssschema: maandag, woensdag en vrijdag. Simpel, effectief, bewezen.`,
      },
      {
        heading: "Gevorderd: 3–5 keer per week",
        body: `Naarmate je vordert, heb je meer volume nodig om te blijven groeien — en dat is moeilijker in 3 sessies te stoppen zonder die sessies heel lang te maken. Een split-schema wordt dan logischer:

**Upper/Lower split (4 dagen)** — Twee upper body en twee lower body sessies per week. Iedere spiergroep wordt 2x getraind. Ideaal voor intermediates.

**Push/Pull/Legs (PPL, 6 dagen)** — Borst/schouders/triceps, rug/biceps, benen — elk twee keer per week. Populair bij gevorderden die veel volume aankunnen.

**4-daagse full body of UpperLower** — Vier sessies per week, goed herstel gepland. Goede balans tussen volume en recuperatie.`,
      },
      {
        heading: "De rol van herstel",
        body: `Hier zit het grote misverstand: spieren groeien NIET tijdens de training, maar erna — tijdens de rust. Training is de prikkel; herstel is het resultaat.

Meer trainen is niet automatisch beter. Als je je herstel niet bijhoudt, train je jezelf uiteindelijk slechter. Tekenen dat je te weinig herstelt:

• Je prestaties stagneren of verslechteren van sessie tot sessie
• Je bent altijd moe, ook na een nachtje goed slapen
• Je hebt weinig motivatie om te gaan trainen
• Je spierpijn duurt langer dan 3–4 dagen na een sessie`,
      },
      {
        heading: "Wat als je maar 2 keer per week kunt?",
        body: `Dan is 2x per week prima — mits je het slim aanpakt. Twee goed geprogrammeerde full body sessies per week geven al significante progressie, ook voor gevorderden. Het bewijs: veel topsporters en drukke professionals bouwen consistente resultaten met twee sessies per week door slim te programmeren en intensiteit hoog te houden.

Consistentie over maanden en jaren verslaat altijd de "perfecte" planning die je niet volhoudt.`,
      },
      {
        heading: "Richtlijn per doel",
        body: `**Spiergroei (hypertrofie)** — 3–5 sessies per week, 10–20 sets per spiergroep per week, elke spiergroep 2x trainen.

**Kracht** — 3–4 sessies per week, focus op samengestelde oefeningen (squat, deadlift, bench, overhead press), lagere reps (3–6), langere rust.

**Vetverlies** — Trainingsfrequentie is minder kritiek dan voeding, maar 3–4 keer trainen helpt spiermassa te behouden tijdens een calorietekort.

**Algehele gezondheid** — 2–3 keer per week krachttraining + 150 minuten matige cardio per week, conform de WHO-richtlijnen.`,
      },
      {
        heading: "Conclusie",
        body: `De optimale trainingsfrequentie bestaat niet in absolute zin — die bestaat alleen in relatie tot jouw herstel, planning en doelen. Begin conservatief, bouw op, luister naar je lichaam.

Wat altijd werkt: minimaal 2 keer per week elke spiergroep trainen, voldoende volume opbouwen over de weken, en consequent blijven. De rest is optimalisatie.`,
      },
    ],
  },

  "herstel-en-spiergroei": {
    tag: "Herstel",
    tagColor: "text-emerald-400 bg-emerald-500/10 border-emerald-500/25",
    title: "Herstel: de meest onderschatte factor in je training",
    subtitle: "Waarom rust net zo belangrijk is als training — en hoe je je herstel actief optimaliseert voor maximale resultaten.",
    readTime: "8 min",
    date: "April 2025",
    sections: [
      {
        heading: "Waarom herstel zo weinig aandacht krijgt",
        body: `In de fitnesswereld draait alles om actie: harder trainen, meer eten, meer supplementen. Herstel voelt als niksdoen — en dat verkoopt slecht. Toch is het de fase waarin spiergroei, krachttoename en vetmetabolisme plaatsvinden. Zonder herstel zijn je trainingssessies investeringen zonder rendement.`,
      },
      {
        heading: "Wat er in je lichaam gebeurt tijdens herstel",
        body: `Tijdens een training veroorzaak je microscopische scheurtjes in de spiervezels. Dit is normaal en noodzakelijk — het is de prikkel voor groei. Direct na de training start een cascade van herstelprocessen:

**Fase 1 (0–2 uur)** — Ontstekingsreactie: immuuncellen stromen naar beschadigde gebieden. Je lichaam herkent de schade.

**Fase 2 (2–24 uur)** — Satellietcellen activeren en beginnen de beschadigde vezels te repareren en te versterken.

**Fase 3 (24–72 uur)** — Proteïnesynthese piekt. Spieren worden dikker en sterker dan voor de training — dit heet supercompensatie.

**Fase 4 (72–96 uur)** — Volledig herstel bij de meeste mensen. Nu is de ideale tijd om dezelfde spiergroep opnieuw te trainen.`,
      },
      {
        heading: "Pijler 1: Slaap",
        body: `Slaap is de meest krachtige herstelinterventie die bestaat — en het is gratis. Tijdens diepe slaap (slow-wave sleep) wordt 70–80% van de dagelijkse groeihormoonproductie vrijgegeven. Dit hormoon is essentieel voor spiergroei en vetverlies.

**Hoeveel slaap heb je nodig?**
De meeste volwassenen hebben 7–9 uur nodig. Atleten en mensen die intensief trainen hebben vaak 8–9 uur nodig voor optimaal herstel.

**Tips voor betere slaapkwaliteit:**
• Zelfde bedtijd en wektijd, ook in het weekend (circadiaan ritme)
• Slaapkamer koel houden: 16–19°C is optimaal
• Geen schermen 60 minuten voor het slapen
• Vermijd cafeïne na 14:00 uur
• Magnesium (glycinaat of bisglycinaat) kan slaapkwaliteit verbeteren

Eén nacht slecht slapen verhoogt cortisolniveaus al significant — het stresshormoon dat spiergroei remt en vetopslag bevordert.`,
      },
      {
        heading: "Pijler 2: Voeding voor herstel",
        body: `Voeding is de bouwstof voor herstel. Zonder de juiste macronutriënten kunnen je spieren letterlijk niet herstellen.

**Eiwitten** — De bouwstenen van spierweefsel. Streef naar 1,6–2,2 gram per kilo lichaamsgewicht per dag. Verdeel dit over 3–5 maaltijden van minimaal 20–40 gram per maaltijd voor maximale proteïnesynthese.

**Koolhydraten** — Vullen de glycogeenvoorraden aan die tijdens training uitgeput raken. Eet complexe koolhydraten (havermout, rijst, zoete aardappel) rondom je training voor optimaal herstel.

**Vetten** — Essentieel voor hormoonproductie (inclusief testosteron). Eet voldoende gezonde vetten (olijfolie, noten, avocado, vette vis).

**Timing** — Een maaltijd met eiwitten en koolhydraten binnen 2 uur na de training maximaliseert het herstelvenster. Een schepje wei-eiwit of een glas melk werkt ook prima.

**Calorietekort en herstel** — Bij vetverlies (caloriebeperking) herstel je trager. Houd dit in je schema rekening: minder volume, meer rusttijden.`,
      },
      {
        heading: "Pijler 3: Actief herstel",
        body: `Rust hoeft niet te betekenen dat je de hele dag op de bank zit. Lichte beweging op rustdagen verbetert de doorbloeding en versnelt het afvoeren van afvalstoffen uit de spieren.

**Goede actieve herstelactiviteiten:**
• 20–30 minuten wandelen of rustig fietsen
• Yoga of stretching (let op: statisch rekken verminder je NIET de spierpijn, maar het verlaagt spanning en verbetert mobiliteit)
• Licht zwemmen
• Foam rolling/myofasciale massage — vermindert spierstijfheid en verhoogt het bewegingsbereik

**Wat werkt NIET:**
• Nog een zware training doen als "hersteltraining"
• Urenlang stretchen als je stijf bent
• Ijsbaden na krachtsessies (koude blootstelling direct na training remt de anabole reactie)`,
      },
      {
        heading: "Pijler 4: Stressmanagement",
        body: `Mentale stress en fysieke stress delen dezelfde herstelaanspraken. Als je werk, relaties of slaapgebrek al veel stress produceren, heeft je lichaam minder capaciteit om te herstellen van training.

**Cortisol** — Het stresshormoon. Chronisch hoge cortisolniveaus remmen testosteron, verhogen vetopslag (met name buikvet) en remmen proteïnesynthese. Training verhoogt cortisol tijdelijk — dat is normaal en noodzakelijk. Maar chronische stress bovenop training is schadelijk.

**Praktische stressreductie:**
• 5–10 minuten diafragmatisch ademen verlaagt cortisol meetbaar
• Dagelijkse blootstelling aan daglicht reguleert cortisol en melatonine
• Sociale verbinding en plezier zijn onderschatte herstelversnellers
• Overweeg meditatie of mindfulness als je gevoelig bent voor stress`,
      },
      {
        heading: "Deload-weken: gepland herstel",
        body: `Na elke 4–8 weken intensief trainen is een deload-week verstandig. Dit is een week met verminderd volume (±50%) of intensiteit, waarbij je de bewegingen wel uitvoert maar het lichaam de kans geeft volledig te herstellen.

**Voordelen van een deload:**
• Volledig herstel van gewrichten, pezen en bindweefsel (die trager herstellen dan spieren)
• Mentale frisheid en motivatie na de deload
• Supercompensatie: na de deload ben je vaak sterker dan voor de deload

**Wanneer deloaden?**
• Gepland: elke 4–6 weken
• Ongepland: als je merkbaar slechter presteert, veel vermoeid bent of tegenzin hebt om te trainen`,
      },
      {
        heading: "Herstelmarkers om bij te houden",
        body: `Je hoeft geen geavanceerde apparatuur te hebben om je herstel bij te houden. Let op:

**Subjectief:**
• Slaapkwaliteit (voelt 7 op een schaal van 10)
• Energieniveau 's ochtends
• Motivatie om te trainen
• Spierpijn die langer dan 4 dagen aanhoudt

**Objectief:**
• Rustpols bij het opstaan (stijging van >5 slagen/min kan overbelasting aangeven)
• Trainingsprestaties (barbell kilo's, herhalingen)
• HRV (hartritmevariabiliteit) via een smartwatch of Oura Ring

Als meerdere markers tegelijk negatief zijn, is rust geen zwakte — het is de slimste investering die je kunt doen.`,
      },
      {
        heading: "Conclusie",
        body: `Herstel is geen bijzaak. Het IS de training — de fase waarin alle harde werk zich omzet in resultaten. Slaap, voeding, actief herstel en stressmanagement zijn de vier pijlers die bepalen hoeveel je terugkrijgt van elke sessie.

Train slim, herstel slimmer. De atleten die het verst komen zijn zelden die het hardst trainen — het zijn diegenen die het beste leren luisteren naar hun lichaam.`,
      },
    ],
  },
};

function ArticleContent({ article }) {
  return (
    <div className="prose prose-invert max-w-none">
      {article.sections.map((section, i) => (
        <div key={i} className={i > 0 ? "mt-10" : ""}>
          <h2 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full shrink-0" style={{ background: GOLD }} />
            {section.heading}
          </h2>
          <div className="text-zinc-400 text-[15px] leading-[1.85] space-y-3">
            {section.body.split("\n\n").map((para, pi) => (
              <p key={pi} className="whitespace-pre-line">
                {para.split(/(\*\*[^*]+\*\*)/).map((part, idx) =>
                  part.startsWith("**") && part.endsWith("**")
                    ? <strong key={idx} className="text-white font-semibold">{part.slice(2, -2)}</strong>
                    : part
                )}
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = ARTICLES[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center text-center px-4">
        <div>
          <p className="text-zinc-500 text-sm mb-4">Artikel niet gevonden</p>
          <button onClick={() => navigate("/")} className="text-[#C9A84C] font-semibold">← Terug naar home</button>
        </div>
      </div>
    );
  }

  const otherArticles = Object.entries(ARTICLES)
    .filter(([s]) => s !== slug)
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#080808]/95 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3.5 flex items-center gap-3">
          <button onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm transition-colors">
            <ArrowLeft size={15} /> Home
          </button>
          <ChevronRight size={13} className="text-zinc-700" />
          <span className="text-zinc-500 text-sm truncate">{article.title}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Article header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${article.tagColor}`}>
              {article.tag}
            </span>
            <span className="text-zinc-600 text-xs flex items-center gap-1.5">
              <Clock size={11} /> {article.readTime} lezen
            </span>
            <span className="text-zinc-600 text-xs flex items-center gap-1.5">
              <Calendar size={11} /> {article.date}
            </span>
          </div>
          <h1 className="text-white font-black text-3xl sm:text-4xl leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed border-l-2 pl-4"
            style={{ borderColor: "rgba(201,168,76,0.4)" }}>
            {article.subtitle}
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/6 mb-10" />

        {/* Article body */}
        <ArticleContent article={article} />

        {/* Author note */}
        <div className="mt-12 p-5 rounded-2xl border border-white/8" style={{ background: "#0f0f0f" }}>
          <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-1">Geschreven door</p>
          <p className="text-white font-semibold text-sm">Samuel van Grondelle</p>
          <p className="text-zinc-500 text-xs mt-0.5">Gecertificeerd Personal Trainer · FitSmith</p>
        </div>

        {/* Other articles */}
        {otherArticles.length > 0 && (
          <div className="mt-14">
            <p className="text-zinc-500 text-xs uppercase tracking-widest font-bold mb-5">Meer artikelen</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {otherArticles.map(([s, a]) => (
                <button key={s} onClick={() => navigate(`/blog/${s}`)}
                  className="text-left p-5 rounded-2xl border border-white/6 hover:border-[#C9A84C]/30 transition-all group"
                  style={{ background: "#0f0f0f" }}>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${a.tagColor}`}>{a.tag}</span>
                  <p className="text-white font-bold text-sm mt-3 mb-1 group-hover:text-[#C9A84C] transition-colors leading-snug">
                    {a.title}
                  </p>
                  <p className="text-zinc-600 text-xs flex items-center gap-1">
                    <Clock size={10} /> {a.readTime}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
